import { useMemo, useState } from 'react';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Dimensions } from 'src/components/ImageGenSettings/components/Dimensions';
import { AppRoutes, RequestPayloadType } from 'src/types';
import { toast } from 'react-toastify';
import { ImageStyleSelect } from 'src/components/ImageGenSettings/components/ImageStyleSelect';
import styles from './ImageGenModifyPanel.module.scss';
import {
  useGetUser,
  useSubmitUserInput,
  useUserSettings,
  useUserSettingsUpdate,
} from 'src/hooks';
import { COMMON_ERROR_TEXT } from 'src/constants';
import { useDeleteAttachment } from 'src/hooks';
import { useRightSidePanelState } from 'src/hooks';
import { SettingKeys, SingleSelectSetting } from 'src/types/settings';
import log from 'src/utils/logger';
import { Button } from 'src/v2/commonComponents/Button';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';
import { ImageGenModelSelect } from '../ImageGenModelSelect';

type ImageGenModifyPanelProps = {
  isOpen?: boolean;
  onClose: () => void;
  messageOriginalQuery?: string;
  messageCleanQuery?: string;
};

export const ImageGenModifyPanel = ({
  isOpen,
  onClose,
  messageOriginalQuery = '',
  messageCleanQuery = '',
}: ImageGenModifyPanelProps) => {
  const navigate = useNavigate();
  const [isRegenerateLoading, setIsRegenerateLoading] =
    useState<boolean>(false);

  const { setting: imageModelSetting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.IMAGE_PRIMARY_MODEL,
  );

  const { setting: dimentionSetting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.IMAGE_DIMENTIONS,
  );

  const { setting: styleSetting } = useUserSettings<SingleSelectSetting>(
    SettingKeys.IMAGE_STYLES,
  );

  const { updateUserSetting } = useUserSettingsUpdate();

  const [imageModelValue, setImageModelValue] = useState<string | undefined>(
    imageModelSetting?.value,
  );
  const [dimentionValue, setDimentionValue] = useState<string | undefined>(
    dimentionSetting?.value,
  );
  const [styleValue, setStyleValue] = useState<string | undefined>(
    styleSetting?.value,
  );

  const { isOpenTier, isFreeTier } = useGetUser();

  const { onSubmitUserInput } = useSubmitUserInput();
  const { onDeleteAllAttachments } = useDeleteAttachment();
  const { isRightPanelExpanded } = useRightSidePanelState();

  const handleSettingsUpdate = async () => {
    try {
      if (!!dimentionValue && dimentionValue !== dimentionSetting?.value) {
        await updateUserSetting({
          value: dimentionValue,
          hideToast: true,
          settingKey: SettingKeys.IMAGE_DIMENTIONS,
        });
      }
      if (!!styleValue && styleValue !== styleSetting?.value) {
        await updateUserSetting({
          value: styleValue,
          hideToast: true,
          settingKey: SettingKeys.IMAGE_STYLES,
        });
      }
      if (!!imageModelValue && imageModelValue !== imageModelSetting?.value) {
        await updateUserSetting({
          value: imageModelValue,
          hideToast: true,
          settingKey: SettingKeys.IMAGE_PRIMARY_MODEL,
        });
      }
    } catch (e) {
      log.error(e);
    }
  };

  const handleRegenerateClick = async () => {
    if (isOpenTier) {
      navigate(AppRoutes.SIGN_UP);
      return;
    }
    if (isFreeTier) {
      navigate(AppRoutes.PAYMENT);
      return;
    }

    try {
      setIsRegenerateLoading(true);
      await onDeleteAllAttachments();
      const value = `/image ${
        messageCleanQuery || messageOriginalQuery
      }`.trim();

      await handleSettingsUpdate();

      onSubmitUserInput({
        userInput: value,
        params: {
          content: value,
          payload_list: [
            {
              payload_type: RequestPayloadType.TEXT,
              content: value,
            },
          ],
        },
      });
    } catch (e) {
      toast.error(COMMON_ERROR_TEXT);
    } finally {
      setIsRegenerateLoading(false);
      onClose();
    }
  };

  const submitTitle = useMemo(() => {
    if (isOpenTier) {
      return 'Sign up to unlock';
    }
    if (isFreeTier) {
      return 'Upgrade to unlock';
    }
    return 'Regenerate';
  }, [isOpenTier, isFreeTier]);

  const buttonId = isFreeTier
    ? 'Image-modify-upgrade'
    : isOpenTier
      ? 'Image-modify-signup'
      : '';

  return (
    <div className={styles.root}>
      <div
        className={cn(styles.content, {
          [styles.mobileView]: isRightPanelExpanded,
        })}
      >
        <div className={styles.field}>
          <ImageGenModelSelect
            setImageModelValue={setImageModelValue}
            imageModelValue={imageModelValue}
          />

          <TextDivider />

          <Dimensions
            selectedValue={dimentionValue}
            onChange={setDimentionValue}
          />

          <TextDivider />

          <ImageStyleSelect
            maxMenuHeight={200}
            selectedValue={styleValue}
            onChange={setStyleValue}
          />
        </div>

        <TextDivider />

        <Button
          color="primary"
          className={styles.regenerateButton}
          type="button"
          onClick={handleRegenerateClick}
          id={buttonId}
          disabled={isRegenerateLoading}
        >
          {submitTitle}
        </Button>
      </div>
    </div>
  );
};
